import { HTMLAttributes, ReactNode, useCallback, useMemo } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import { cx } from '@emotion/css';
import { useTheme } from '@mui/material/styles';
import useMediaQuery from '@mui/material/useMediaQuery';
import { useModal } from 'mui-modal-provider';
import { useQuery } from '@apollo/client';

import { CvModalProps } from './cv-modal-info.models';
import { useStyles } from './cv-modal-info.styles';
import { Modal, ResponsiveWidth } from '../../../../common';
import { Button } from '../../../../common/button-new';
import { ReasonList } from './reason-list';
import { ContactUs } from './contact-us';
import { Steps } from './steps';
import { CvCallbackRequestModal } from '../cv-callback-request-modal';
import { CvCallbackRequestSuccess } from '../cv-callback-request-success';
import { ActiveAppointmentModal } from '../../active-appointment-modal';
import { blurActiveElement } from '../../../../../utils';
import { GET_IS_USER_LOGGED_IN } from '../../../../../graphql';
import { GET_APPOINTMENTS } from '../../../../../graphql/appointments/queries/get-appointments';
import { IConsumerAppointments } from '../../../../../graphql/appointments/models/get-appointments.models';
import { isAppointmentContentShow } from '../../../../../utils/appointment/is-appoinment-content-show';
import headerImage from '../../../../../assets/images/cv/cv-modal-header.jpg';
import headerImageMobile from '../../../../../assets/images/cv/cv-modal-header-mobile.jpg';
import { Preloader } from '../../../../common/preloader';

const preloaderSize = '1.5rem';

export const CvModalInfo = ({
  onNavigateToEcp, points, fromDays, toDays, endDate, onClose, onNavigateToAppointment, ...props
}: CvModalProps): JSX.Element => {
  const intl = useIntl();
  const classes = useStyles();
  const theme = useTheme();
  const { showModal } = useModal();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  const { data: { user: { isLoggedIn } } } = useQuery(GET_IS_USER_LOGGED_IN);
  const { data: appointmentsData, loading } = useQuery<IConsumerAppointments>(
    GET_APPOINTMENTS, { skip: !isLoggedIn, fetchPolicy: 'network-only' },
  );
  const appointments = appointmentsData?.allAppointmentsByAuthId?.edges || [];
  const activeAppointment = appointments.find(
    ({ node }) => isAppointmentContentShow(node?.date, node?.status),
  )?.node;

  const headerImg = useMemo((): string => {
    return isMobile ? headerImageMobile : headerImage;
  }, [isMobile]);

  const handleClose = useCallback((): void => {
    blurActiveElement();
    onClose?.();
  }, [onClose]);

  const handleAction = useCallback((): void => {
    blurActiveElement();

    if (activeAppointment) {
      showModal(ActiveAppointmentModal, {
        appointmentType: activeAppointment.appointmentType,
        date: activeAppointment.date,
        onView: onNavigateToAppointment,
      });
      return;
    }

    onNavigateToEcp();
  }, [onClose, onNavigateToEcp, activeAppointment, showModal, onNavigateToAppointment]);

  const handleCallbackClick = useCallback((): void => {
    blurActiveElement();
    const callbackModal = showModal(CvCallbackRequestModal, {
      onSuccess: () => {
        callbackModal.hide();

        showModal(CvCallbackRequestSuccess, {});
      },
    });
  }, [showModal]);

  return (
    <Modal
      {...props}
      onClose={handleClose}
      responsiveWidth={ResponsiveWidth.lg}
      propsPaperStyles={classes.propsPaperStyles}
      propsContentStyles={classes.propsContentStyles}
      data-testid="cv-modal-info"
      PaperProps={{ 'data-testid': 'cv-modal-paper' } as HTMLAttributes<HTMLDivElement>}
      content={(
        <>
          <div className={classes.section}>
            <h2 className={cx(classes.title, classes.uppercase)}>
              {intl.formatMessage({ id: 'controlVisit.modal.title' }, { noBreak: <>&nbsp;</>, points })}
            </h2>
            <img
              data-testid="header-image"
              src={headerImg}
              alt=""
              width={80}
              height={80}
              className={classes.header}
            />
            <ReasonList points={points}/>
          </div>
          <div className={classes.section}>
            <h3
              className={cx('title-medium', classes.uppercase)}
              data-testid="title-steps"
            >
              <FormattedMessage id="controlVisit.modal.steps.title" />
            </h3>
            <Steps points={points} date={endDate}></Steps>
          </div>
          <div className={classes.section}>
            <h3
              className={cx('title-medium', classes.uppercase)}
              data-testid="title-dates"
            >
              <FormattedMessage id="controlVisit.modal.dates.title" />
            </h3>
            <div className={classes.dates}>
              <p>{intl.formatMessage(
                { id: 'controlVisit.modal.dates-1' },
                {
                  strong: (chunk: ReactNode) => <strong>{chunk}</strong>,
                  fromDays,
                  toDays,
                },
              )}</p>
              <p>{intl.formatMessage(
                { id: 'controlVisit.modal.dates-2' },
                { noBreak: <>&nbsp;</> },
              )}</p>
            </div>
          </div>
          <div className={classes.section}>
            <h3
              className={cx('title-medium', classes.uppercase)}
              data-testid="title-contact-us"
            >
              <FormattedMessage id="contactUs.title" />
            </h3>
            <p data-testid="contact-us-description">
              <FormattedMessage id="contactUs.description"/>
            </p>
            <ContactUs onCallBackClick={handleCallbackClick} />
          </div>
        </>
      )}
      footer={(
        <Button
          data-testid="ecp-link"
          onClick={handleAction}
          style="cv"
          rootClasses={classes.button}
          disabled={loading}
        >
          {loading && <Preloader size={preloaderSize} isInner />}
          <FormattedMessage id="common.cv.action" values={{ noBreak: <>&nbsp;</> }} />
        </Button>
      )}
      footerStyles={classes.footer}
    />
  );
};
